import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { integrationApiService, ShopifyTaxonomyOption } from '../../services/integrationApi';

const GID_PATTERN = /^gid:\/\/shopify\/TaxonomyCategory\/[a-z]{2}(-\d+)*$/;
/** The server's cap on one lookup (`MAX_TAXONOMY_LOOKUP_IDS`); more GIDs go in several calls. */
export const MAX_TAXONOMY_LOOKUP = 300;
/**
 * Most characters of percent-encoded ids one lookup URL carries. The ids ride in a GET query, and
 * Node refuses a request whose headers (URL plus auth) pass 16 KB, so a batch is cut by encoded
 * length well before that, not only by count.
 */
export const MAX_TAXONOMY_LOOKUP_URL_CHARS = 6000;

/** `ids` split into lookups within both the server's count cap and the URL budget. */
export const taxonomyLookupBatches = (ids: string[]): string[][] => {
  const batches: string[][] = [];
  let batch: string[] = [];
  let chars = 0;
  for (const id of ids) {
    // The id plus its encoded comma separator.
    const size = encodeURIComponent(id).length + 3;
    if (batch.length > 0 && (batch.length >= MAX_TAXONOMY_LOOKUP || chars + size > MAX_TAXONOMY_LOOKUP_URL_CHARS)) {
      batches.push(batch);
      batch = [];
      chars = 0;
    }
    batch.push(id);
    chars += size;
  }
  if (batch.length > 0) batches.push(batch);
  return batches;
};

/**
 * The curated category options plus every GID in `gids` that is not one of them, labelled with
 * Shopify's full breadcrumb, looked up in batches (at most 300, and short enough for one GET URL) so no GID is left out. A store can
 * pick any category of the full taxonomy, so a stored or drafted GID outside the curated list still
 * reads as its breadcrumb everywhere `taxonomyLabel` is used, never as a bare id.
 */
export function useShopifyTaxonomyOptions(
  base: ShopifyTaxonomyOption[],
  gids: ReadonlyArray<string | null | undefined>,
): ShopifyTaxonomyOption[] {
  const known = useMemo(() => new Set(base.map((option) => option.gid)), [base]);
  const missingKey = useMemo(
    () =>
      Array.from(new Set(gids.filter((gid): gid is string => typeof gid === 'string' && GID_PATTERN.test(gid) && !known.has(gid))))
        .sort()
        .join(','),
    [gids, known],
  );
  const lookup = useQuery({
    queryKey: ['shopify-taxonomy', 'lookup', missingKey],
    queryFn: async () => {
      const batches = taxonomyLookupBatches(missingKey.split(','));
      return (await Promise.all(batches.map((batch) => integrationApiService.lookupShopifyTaxonomy(batch)))).flat();
    },
    enabled: missingKey.length > 0,
    staleTime: Infinity,
    retry: false,
    placeholderData: keepPreviousData,
  });
  const found = missingKey ? lookup.data : undefined;
  return useMemo(() => {
    const extras = (found || [])
      .filter((node) => !known.has(node.gid))
      .map((node) => ({ gid: node.gid, label: node.fullName, fullName: node.fullName }));
    return extras.length > 0 ? [...base, ...extras] : base;
  }, [base, known, found]);
}
